/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * The filter bar's multi-select control.
 *
 * Deliberately not the shared react-select `Dropdown`: with a few hundred companies, chips inside
 * the control push the filter bar to three or four rows and the bar stops being scannable. Here the
 * control is a fixed-height button that always reads as its filter name ("Companies"), carrying only
 * a count; what is selected lives inside the menu, pinned to the top, where it is checkable rather
 * than a wall of × buttons.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'

import { FiCheck, FiChevronDown } from 'react-icons/fi'

export interface FilterSelectOption {
  value: string
  label: string
  /** Muted text rendered after the label, and searched alongside it — the company hierarchy path. */
  hint?: string
  /** Right-aligned muted value — a user's change count. */
  meta?: string
  icon?: React.ReactNode
}

interface FilterSelectProps {
  /** Always visible on the control, so the bar reads as a set of named filters. */
  label: string
  options: FilterSelectOption[]
  selected: string[]
  onChange: (next: string[]) => void
  searchable?: boolean
  emptyText?: string
  /** Selections that cannot be given up: the last one stays checked. Company uses 1. */
  minSelected?: number
  className?: string
}

const Checkbox: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span
    aria-hidden
    className={`flex size-4 shrink-0 items-center justify-center rounded-[3px] border ${
      checked ? 'border-primary bg-primary text-primary-content' : 'border-base-content/40'
    }`}
  >
    {checked && <FiCheck className="text-[11px]" />}
  </span>
)

export const FilterSelect: React.FC<FilterSelectProps> = ({
  label,
  options,
  selected,
  onChange,
  searchable = false,
  emptyText = 'No options.',
  minSelected = 0,
  className = '',
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // A stale query would silently hide options the next time the menu opens.
  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const selectedSet = useMemo(() => new Set(selected), [selected])
  const atMinimum = selected.length <= minSelected

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => `${o.label} ${o.hint ?? ''}`.toLowerCase().includes(q))
  }, [options, query])

  const chosen = matches.filter((o) => selectedSet.has(o.value))
  const rest = matches.filter((o) => !selectedSet.has(o.value))

  const toggle = (option: FilterSelectOption) => {
    if (selectedSet.has(option.value)) {
      // Removing the last selection would leave the filter meaningless, so it is simply inert.
      if (atMinimum) return
      onChange(selected.filter((v) => v !== option.value))
    } else {
      onChange([...selected, option.value])
    }
  }

  const renderOption = (option: FilterSelectOption) => {
    const checked = selectedSet.has(option.value)
    const locked = checked && atMinimum
    return (
      <li key={option.value}>
        <button
          type="button"
          role="option"
          aria-selected={checked}
          aria-disabled={locked}
          title={
            locked
              ? `At least one ${label.replace(/s$/, '').toLowerCase()} must be selected`
              : `${option.label}${option.hint ?? ''}`
          }
          onClick={() => toggle(option)}
          className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${
            locked ? 'cursor-not-allowed opacity-60' : 'hover:bg-base-200'
          }`}
        >
          <Checkbox checked={checked} />
          {option.icon && <span className="shrink-0 text-base-content/70">{option.icon}</span>}
          <span className="min-w-0 flex-1 truncate">
            {option.label}
            {option.hint && <span className="text-base-content/50">{option.hint}</span>}
          </span>
          {option.meta && <span className="shrink-0 text-xs text-base-content/50">{option.meta}</span>}
        </button>
      </li>
    )
  }

  const sectionClass = 'px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-base-content/50'

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`flex h-8 w-full items-center justify-between gap-2 rounded-field border bg-base-100 px-2.5 text-sm ${
          selected.length > 0 ? 'border-primary' : 'border-base-300 hover:border-base-content/40'
        }`}
      >
        <span className="truncate">{label}</span>
        <span className="flex shrink-0 items-center gap-1.5">
          {selected.length > 0 && (
            <span className="rounded-selector bg-primary px-1.5 text-[10px] font-semibold leading-4 text-primary-content">
              {selected.length}
            </span>
          )}
          <FiChevronDown aria-hidden className={`text-base-content/50 ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-40 mt-1 w-full min-w-[240px] overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-lg">
          {searchable && (
            <div className="border-b border-base-200 p-1.5">
              <input
                autoFocus
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}…`}
                aria-label={`Search ${label.toLowerCase()}`}
                className="input input-xs input-bordered h-8 w-full"
              />
            </div>
          )}

          <ul role="listbox" aria-multiselectable aria-label={label} className="max-h-64 overflow-y-auto pb-1">
            {matches.length === 0 && <li className="px-3 py-3 text-xs text-base-content/60">{emptyText}</li>}

            {/* Selected first: with a long list the user must not have to hunt for what is on. */}
            {chosen.length > 0 && (
              <>
                <li className={sectionClass}>Selected ({selected.length})</li>
                {chosen.map(renderOption)}
              </>
            )}

            {rest.length > 0 && (
              <>
                {chosen.length > 0 && (
                  <li className={`${sectionClass} mt-1 border-t border-base-200`}>All {label.toLowerCase()}</li>
                )}
                {rest.map(renderOption)}
              </>
            )}
          </ul>

          {(minSelected === 0 || options.length > 0) && (
            <div className="flex items-center justify-between gap-2 border-t border-base-200 px-2 py-1.5">
              <button
                type="button"
                onClick={() => onChange(options.map((o) => o.value))}
                disabled={options.length === 0 || selected.length === options.length}
                className="rounded-field px-1.5 py-0.5 text-xs font-medium text-accent hover:bg-base-200 disabled:pointer-events-none disabled:opacity-40"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => onChange(minSelected > 0 ? selected.slice(0, minSelected) : [])}
                disabled={selected.length <= minSelected}
                className="rounded-field px-1.5 py-0.5 text-xs font-medium text-accent hover:bg-base-200 disabled:pointer-events-none disabled:opacity-40"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
